import React from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Download, Palette, Type, PenLine, Smartphone, Package,
  Image, ExternalLink, Copy, Check, MessageCircle, Phone,
  Globe, FileArchive, Printer, ArrowRight, Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;
const SITE = API;
const WA = "https://wa.me/+393273839782";

const LOGOS = [
  { name: "schedabusiness — Marchio Principale", file: "SBP-Marchio-Principale.png", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/dbdc3d6e1c39252d006c0263aee55f5435b0800715d0eb190855a984abad68b9.png" },
  { name: "SBPDA — Digital Academy", file: "SBPDA-Digital-Academy.png", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/bf3826606211f86b61b02b4db2373f48f304a8a0eba3f27fa52eebef60a2c70c.png" },
  { name: "Virtual Tour 360", file: "VirtualTour-360.png", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/f0a377102406c966d6d0dbddd1fe498101dc4e87d87ac212a879bb6e6da86bf9.png" },
];

const COLORS = [
  { name: "Blu Google", hex: "#4285F4", pantone: "660 C" },
  { name: "Rosso Google", hex: "#EA4335", pantone: "1795 C" },
  { name: "Giallo Google", hex: "#FBBC04", pantone: "7549 C" },
  { name: "Verde Google", hex: "#34A853", pantone: "7481 C" },
  { name: "Arancio schedabusiness", hex: "#FF6B35", pantone: "165 C" },
  { name: "Terre di Siena", hex: "#7B2D26", pantone: "—" },
];

const PENS = [
  { name: "Blu — Start", hex: "#3B82F6", pantone: "PMS 2727 C", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/23eeda59e4b6716b81902356d7ad31d605f63a57a12df30faced2fd506cd0ded.png" },
  { name: "Rosso — Plus", hex: "#EF4444", pantone: "PMS 1797 C", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/a5defb491e7193c8ac5a2c9179b045961fda09e83d39f27f1a945c30c451cca2.png" },
  { name: "Ambra — Rooftop", hex: "#F59E0B", pantone: "PMS 130 C", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/1a3b8ef3b70d95cc117df0f81b770ffeb73da4315d4096d6f0f38d33c1897754.png" },
  { name: "Teal — Virtual Tour", hex: "#0D9488", pantone: "PMS 7473 C", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/bd39066b9f3bce302c8ab913729500c56d0bafbd82fd91dce6e29efc0e4cd881.png" },
];

const STAND_IMAGES = [
  { name: "Base Smartphone (close-up)", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/b71cf704d84e79a2e485dfbd389f625f88f2d81657315904b856aac6606f8373.png" },
  { name: "Base Smartphone (scrivania)", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/ba755fa5311e973423ce087f42c08363f8ac45c3c8e460a3600e6b7da6745150.png" },
];

const ALL_PENS_IMG = "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/1af9598c3ab41814b03fadda5eb969e204577a2e529233163652411316dad110.png";

function CopyHex({ hex }) {
  const [ok, setOk] = React.useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(hex); setOk(true); setTimeout(() => setOk(false), 1500); }}
      className="ml-1 text-gray-400 hover:text-white transition-colors" title="Copia HEX">
      {ok ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

export default function AgencyPage() {
  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="agency-page">

      {/* HEADER */}
      <header className="border-b border-white/10 py-10 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-black font-google mb-2" data-testid="agency-title">
            <span className="text-[#EA4335]">S</span><span className="text-[#FBBC04]">B</span><span className="text-[#34A853]">P</span>
            <span className="text-white"> — Brand Kit</span>
          </h1>
          <p className="text-gray-400 text-sm max-w-md mx-auto">
            Risorse per agenzie, tipografie e partner. Loghi, colori, font, specifiche prodotto e file scaricabili.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
            <Button
              size="lg"
              className="bg-[#ff6b35] hover:bg-[#e55a2b] text-white rounded-full px-8 text-sm font-bold w-full sm:w-auto"
              onClick={() => window.open(`${API}/api/brand-kit/download`, '_blank')}
              data-testid="agency-download-zip"
            >
              <FileArchive className="w-4 h-4 mr-2" /> Scarica Tutto (ZIP)
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 rounded-full px-8 text-sm w-full sm:w-auto"
              onClick={() => window.open(`${SITE}/brand-kit`, '_blank')}
              data-testid="agency-full-brandkit"
            >
              <Printer className="w-4 h-4 mr-2" /> Scheda Completa Tipografia
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-10">

        {/* CONTATTO */}
        <section className="bg-white/5 rounded-2xl p-5 border border-white/10" data-testid="agency-contact">
          <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Contatto Diretto</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <p className="text-sm font-bold">Riccardo Rossi</p>
                <p className="text-gray-500 text-xs">+39 327 383 9782</p>
              </div>
            </div>
            <a href={WA} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:bg-white/5 rounded-xl p-1 -m-1 transition-colors">
              <div className="w-9 h-9 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <p className="text-sm font-bold">WhatsApp</p>
                <p className="text-green-400 text-xs">Scrivimi</p>
              </div>
            </a>
            <a href={SITE} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:bg-white/5 rounded-xl p-1 -m-1 transition-colors">
              <div className="w-9 h-9 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Globe className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-bold">Sito Web</p>
                <p className="text-blue-400 text-xs">Visita</p>
              </div>
            </a>
          </div>
        </section>

        {/* LOGHI */}
        <section data-testid="agency-logos">
          <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
            <Image className="w-4 h-4 text-[#ff6b35]" /> Loghi (PNG — Sfondo Trasparente)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {LOGOS.map(l => (
              <div key={l.name} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden group hover:border-[#ff6b35]/40 transition-colors">
                <div className="p-4 flex items-center justify-center h-28" style={{ backgroundImage: "linear-gradient(45deg, rgba(255,255,255,0.03) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.03) 75%), linear-gradient(45deg, rgba(255,255,255,0.03) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.03) 75%)", backgroundSize: "12px 12px", backgroundPosition: "0 0, 6px 6px" }}>
                  <img src={l.url} alt={l.name} className="max-h-20 object-contain" />
                </div>
                <div className="px-3 py-2.5 border-t border-white/10 flex items-center justify-between">
                  <p className="text-[11px] font-bold text-gray-300 truncate mr-2">{l.name}</p>
                  <a href={l.url} target="_blank" rel="noopener noreferrer" download={l.file}
                    className="text-[#ff6b35] hover:text-[#e55a2b] flex items-center gap-1 text-[10px] font-bold flex-shrink-0">
                    <Download className="w-3 h-3" /> PNG
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* COLORI */}
        <section data-testid="agency-colors">
          <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
            <Palette className="w-4 h-4 text-[#ff6b35]" /> Palette Colori
          </h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {COLORS.map(c => (
              <div key={c.hex} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                <div className="h-12 rounded-t-xl" style={{ backgroundColor: c.hex }} />
                <div className="p-2">
                  <p className="text-[10px] font-bold text-gray-300">{c.name}</p>
                  <p className="text-[9px] text-gray-500 flex items-center">{c.hex} <CopyHex hex={c.hex} /></p>
                  <p className="text-[9px] text-gray-600">{c.pantone}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FONT */}
        <section data-testid="agency-fonts">
          <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
            <Type className="w-4 h-4 text-[#ff6b35]" /> Tipografia
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <p className="font-google text-xl font-black">
                <span className="text-[#EA4335]">S</span><span className="text-[#FBBC04]">B</span><span className="text-[#34A853]">P</span>
              </p>
              <p className="text-sm font-bold text-gray-300 mt-1">Poppins</p>
              <p className="text-[10px] text-gray-500">Black (900) logo · Bold (700) titoli · Regular (400) corpo</p>
              <a href="https://fonts.google.com/specimen/Poppins" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] text-[#ff6b35] font-bold mt-2">
                <ExternalLink className="w-3 h-3" /> Google Fonts
              </a>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <p className="font-typewriter text-xl text-teal-400">Virtual Tour 360</p>
              <p className="text-sm font-bold text-gray-300 mt-1">Special Elite</p>
              <p className="text-[10px] text-gray-500">Uso esclusivo Virtual Tour — stile macchina da scrivere</p>
              <a href="https://fonts.google.com/specimen/Special+Elite" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] text-teal-500 font-bold mt-2">
                <ExternalLink className="w-3 h-3" /> Google Fonts
              </a>
            </div>
          </div>
        </section>

        {/* PENNE */}
        <section data-testid="agency-pens">
          <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
            <PenLine className="w-4 h-4 text-[#ff6b35]" /> Penne schedabusiness — 4 Varianti
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            {PENS.map(p => (
              <div key={p.hex} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                <div className="bg-white/[0.02] p-2">
                  <img src={p.url} alt={`Penna ${p.name}`} className="w-full h-24 object-cover rounded-lg" />
                </div>
                <div className="p-2">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: p.hex }} />
                    <span className="text-[10px] font-bold text-gray-300 truncate">{p.name}</span>
                  </div>
                  <p className="text-[9px] text-gray-500">{p.hex} · {p.pantone}</p>
                  <a href={p.url} target="_blank" rel="noopener noreferrer" download
                    className="inline-flex items-center gap-1 text-[10px] text-[#ff6b35] font-bold mt-1">
                    <Download className="w-3 h-3" /> HD
                  </a>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-3">
            <div className="flex items-center gap-3">
              <img src={ALL_PENS_IMG} alt="4 penne gruppo" className="w-24 h-16 object-cover rounded-lg flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-gray-300">Specifiche Incisione</p>
                <p className="text-[10px] text-gray-500 mt-0.5">Laser · "schedabusiness" Poppins Black 900 · Bianco su alluminio · Centro corpo, lato opposto clip · ~30x8mm</p>
              </div>
              <a href={ALL_PENS_IMG} target="_blank" rel="noopener noreferrer" download
                className="text-[#ff6b35] flex-shrink-0">
                <Download className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* STAND */}
        <section data-testid="agency-stand">
          <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-[#ff6b35]" /> Base Smartphone Bambu
          </h2>
          <div className="grid grid-cols-2 gap-3 mb-3">
            {STAND_IMAGES.map(s => (
              <div key={s.name} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                <img src={s.url} alt={s.name} className="w-full h-32 object-cover" />
                <div className="px-3 py-2 flex items-center justify-between">
                  <span className="text-[10px] font-bold text-gray-400 truncate mr-2">{s.name}</span>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" download
                    className="text-[#ff6b35] flex-shrink-0">
                    <Download className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
            Simile a IKEA BERGENES · Bambu FSC · 75x75x12mm · Incisione laser "schedabusiness" Poppins Black · ~20x10mm frontale
          </p>
        </section>

        {/* LINK RAPIDI */}
        <section data-testid="agency-links">
          <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#ff6b35]" /> Link Rapidi
          </h2>
          <div className="space-y-2">
            {[
              { href: `${SITE}/brand-kit`, label: "Scheda Completa Tipografia", desc: "Loghi, colori, font, specifiche — stampabile A4", icon: Printer },
              { href: `${SITE}/merchandising`, label: "Catalogo Merchandising", desc: "2 collezioni, 5 prodotti, mockup HD", icon: Package },
              { href: `${SITE}/links`, label: "schedabusiness Hub", desc: "Tutti i servizi e link in un'unica pagina", icon: Globe },
              { href: `${SITE}`, label: "Sito Web schedabusiness", desc: "Homepage principale", icon: Globe },
              { href: "https://fonts.google.com/specimen/Poppins", label: "Font Poppins", desc: "Google Fonts — download gratuito", icon: Type },
              { href: "https://fonts.google.com/specimen/Special+Elite", label: "Font Special Elite", desc: "Solo per Virtual Tour", icon: Type },
              { href: WA, label: "WhatsApp Riccardo", desc: "+39 327 383 9782", icon: MessageCircle },
            ].map(l => (
              <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 bg-white/5 border border-white/10 rounded-xl hover:border-[#ff6b35]/40 hover:bg-white/[0.07] transition-all group"
                data-testid={`agency-link-${l.label.toLowerCase().replace(/\s/g,'-')}`}>
                <div className="w-8 h-8 bg-[#ff6b35]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <l.icon className="w-4 h-4 text-[#ff6b35]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-200 group-hover:text-white transition-colors">{l.label}</p>
                  <p className="text-[10px] text-gray-500 truncate">{l.desc}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-[#ff6b35] transition-colors flex-shrink-0" />
              </a>
            ))}
          </div>
        </section>

        {/* QR CODE /scopri */}
        <section className="text-center" data-testid="agency-qr-scopri">
          <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">QR Code — Condividi schedabusiness</h2>
          <div className="inline-block bg-white rounded-2xl p-5 shadow-xl relative">
            <QRCodeSVG
              value={`${SITE}/scopri`}
              size={140}
              fgColor="#1a1a2e"
              bgColor="#ffffff"
              level="M"
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-white rounded-full p-1.5 shadow">
                <svg className="w-5 h-5" viewBox="0 0 48 48" fill="none">
                  <path d="M24 4C15.163 4 8 11.163 8 20C8 31 24 44 24 44C24 44 40 31 40 20C40 11.163 32.837 4 24 4Z" fill="#4285F4"/>
                  <circle cx="24" cy="20" r="8" fill="white"/>
                  <circle cx="24" cy="20" r="4" fill="#4285F4"/>
                </svg>
              </div>
            </div>
          </div>
          <p className="text-gray-600 text-[10px] mt-2">{SITE}/scopri</p>
          <p className="text-gray-700 text-[9px] mt-0.5">Scansiona per l'esperienza interattiva schedabusiness</p>
        </section>

        {/* CTA SCARICA ZIP */}
        <section className="bg-gradient-to-r from-[#ff6b35]/20 to-[#ff6b35]/5 border border-[#ff6b35]/30 rounded-2xl p-6 text-center" data-testid="agency-zip-cta">
          <FileArchive className="w-8 h-8 text-[#ff6b35] mx-auto mb-3" />
          <h2 className="text-lg font-bold font-google mb-1">Scarica tutto in un click</h2>
          <p className="text-gray-400 text-xs mb-4">Loghi PNG, foto penne HD, base smartphone — tutto in un archivio ZIP organizzato per cartelle</p>
          <Button
            size="lg"
            className="bg-[#ff6b35] hover:bg-[#e55a2b] text-white rounded-full px-10 text-sm font-bold"
            onClick={() => window.open(`${API}/api/brand-kit/download`, '_blank')}
            data-testid="agency-download-zip-bottom"
          >
            <Download className="w-4 h-4 mr-2" /> Scarica ZIP — schedabusiness Brand Kit
          </Button>
        </section>

        {/* FOOTER */}
        <footer className="text-center pt-4 pb-8">
          <p className="font-black text-sm font-google">
            <span className="text-[#EA4335]">S</span><span className="text-[#FBBC04]">B</span><span className="text-[#34A853]">P</span>
            <span className="text-white"> — Business Profile Solutions</span>
          </p>
          <p className="text-gray-600 text-xs mt-1">Riccardo Rossi · Siena · <strong>Made in Toscana</strong>, <strong>Italy</strong></p>
          <p className="text-gray-700 text-[9px] mt-2">Documento riservato — Feb 2026</p>
        </footer>
      </div>
    </div>
  );
}
